/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Gift, Loader2, Send, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'

import { AnimateInView } from '@/components/animate-in-view'
import { TrialFeedback } from './trial-feedback'
import { useDirection } from '@/context/direction-provider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { api } from '@/lib/api'

interface TrialChatCardProps {
  delay?: number
  className?: string
}

interface TrialResult {
  reply?: string
  remaining?: number
  limit?: number
  message?: string
  limitReached?: boolean
}

const DEFAULT_WRAPPER_CLASS = 'w-full md:col-span-2'

const CARD_SHELL_CLASS =
  'w-full overflow-hidden rounded-2xl border backdrop-blur-sm border-border/60 bg-white/95 shadow-[0_20px_50px_-25px_rgba(15,23,42,0.18)] dark:border-white/[0.06] dark:bg-[#0b0f17]/95 dark:shadow-[0_20px_60px_-25px_rgba(0,0,0,0.7)]'

export function TrialChatCard({ delay = 0, className }: TrialChatCardProps) {
  const { t } = useTranslation()
  const { dir } = useDirection()
  const [question, setQuestion] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<TrialResult | null>(null)

  const limitReached = result?.limitReached === true

  async function handleSend() {
    if (!question.trim() || loading || limitReached) return
    setLoading(true)
    try {
      const res = await api.post(
        '/api/public-trial-chat',
        { message: question.trim() },
        { skipBusinessError: true } as Record<string, unknown>
      )
      setResult({
        reply: res.data.reply,
        remaining: res.data.remaining,
        limit: res.data.limit,
      })
      setQuestion('')
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status
      const data = (err as { response?: { data?: Record<string, unknown> } })
        ?.response?.data
      if (status === 429) {
        setResult({
          limitReached: true,
          message: data?.message as string | undefined,
        })
      } else {
        setResult({
          message: t('Something went wrong. Please try again.'),
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const remaining = typeof result?.remaining === 'number' ? result.remaining : 10

  return (
    <AnimateInView
      delay={delay}
      animation='scale-in'
      className={className ?? DEFAULT_WRAPPER_CLASS}
    >
      <div className={CARD_SHELL_CLASS} dir={dir}>
        {/* Header — mirrors the terminal demo's tab bar */}
        <div className='flex items-center gap-2.5 border-b px-5 py-3 border-border/40 bg-muted/40 dark:border-white/[0.04] dark:bg-white/[0.03]'>
          <span className='flex size-6 items-center justify-center rounded-md bg-blue-500/10 text-blue-600 dark:text-blue-400'>
            <Sparkles className='size-3.5' />
          </span>
          <div className='flex flex-col'>
            <span className='text-foreground/85 text-[15px] font-semibold'>
              {t('Try it free — no signup required')}
            </span>
            <span className='text-foreground/45 text-[11px]'>
              {t('Kia API trial assistant')}
            </span>
          </div>
          <div className='mr-auto flex items-center gap-2'>
            <span className='inline-block size-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.45)]' />
            <span className='text-foreground/40 font-mono text-[10px] tracking-wider uppercase'>
              {t('live demo')}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className='px-5 py-4'>
          <p className='text-foreground/60 mb-3 text-[14.5px] leading-relaxed'>
            {t('Ask up to 10 questions instantly. No account, no commitment.')}
          </p>

          {!limitReached ? (
            <div className='space-y-3'>
              <div className='flex gap-2'>
                <Input
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSend()
                    }
                  }}
                  placeholder={t('Ask anything...')}
                  disabled={loading}
                  className='flex-1'
                />
                <Button
                  onClick={handleSend}
                  disabled={loading || !question.trim()}
                  size='icon'
                >
                  {loading ? (
                    <Loader2 className='size-4 animate-spin' />
                  ) : (
                    <Send className='size-4' />
                  )}
                </Button>
              </div>

              {result?.reply && (
                <div className='border-border/40 bg-muted/20 dark:bg-white/[0.02] rounded-lg border p-3 font-mono text-[14.5px] leading-relaxed'>
                  {result.reply}
                </div>
              )}
              {result?.message && !result.reply && (
                <p className='text-muted-foreground text-xs'>
                  {result.message}
                </p>
              )}
            </div>
          ) : (
            <div className='border-border/40 bg-muted/20 dark:bg-white/[0.02] flex flex-col items-start gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between'>
              <div className='flex items-start gap-2'>
                <Gift className='mt-0.5 size-4 shrink-0 text-blue-500' />
                <p className='text-[14.5px]'>
                  {result?.message ||
                    t(
                      "You've used all 10 free questions. Sign up now and get a welcome credit bonus!"
                    )}
                </p>
              </div>
              <Button
                render={<Link to='/register' />}
                size='sm'
                className='shrink-0'
              >
                {t('Sign up & claim bonus')}
              </Button>
            </div>
          )}
        </div>

        {/* Footer — mirrors the terminal demo's stats bar */}
        <div className='flex items-center justify-between border-t px-5 py-2.5 border-border/40 bg-muted/40 dark:border-white/[0.05] dark:bg-white/[0.03]'>
          <div className='text-foreground/40 flex items-center gap-3 text-[10px] tabular-nums'>
            <span className='flex items-center gap-1'>
              <span className='font-mono'>{remaining}</span>
              <span className='tracking-wider uppercase'>
                {t('questions left')}
              </span>
            </span>
            <span className='bg-foreground/15 size-1 rounded-full' />
            <span className='flex items-center gap-1'>
              <span className='tracking-wider uppercase'>{t('model')}</span>
              <span className='font-mono'>Gemini</span>
            </span>
          </div>
          <Link
            to='/register'
            className='font-mono text-[10px] font-bold tracking-wider text-blue-600 uppercase hover:underline dark:text-blue-400'
          >
            {t('Start signup')}
          </Link>
        </div>

        {/* Satisfaction survey — same card, own bordered row */}
        <div className='border-t px-5 py-3 border-border/40 bg-muted/20 dark:border-white/[0.05] dark:bg-white/[0.02]'>
          <TrialFeedback />
        </div>
      </div>
    </AnimateInView>
  )
}
